import { Link } from "react-router-dom";
import { TESTIDS } from "@/lib/testIds";

// Footer uses a full lockup: sprout mark + serif wordmark. The full ImpactVest
// name remains in the footer per brand direction.
function FooterLockup() {
  return (
    <span className="inline-flex items-center gap-3">
      <svg viewBox="0 0 120 120" width="36" height="36" aria-hidden="true">
        <defs>
          <linearGradient id="ivGoldFooter" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#d9c48c" />
            <stop offset="45%" stopColor="#c9a869" />
            <stop offset="100%" stopColor="#8f7538" />
          </linearGradient>
        </defs>
        <circle cx="60" cy="60" r="54" fill="url(#ivGoldFooter)" />
        <path d="M60 84 C 38 84, 28 66, 30 44 C 30 42, 32 40, 34 41 C 54 44, 60 60, 60 84 Z" fill="#0e5a3e" />
        <path d="M60 84 C 60 60, 70 42, 90 34 C 92 33, 94 34, 94 36 C 94 60, 84 82, 60 84 Z" fill="#0e5a3e" />
      </svg>
      <span className="font-serif-display text-[22px] tracking-[0.01em] text-iv-fg">ImpactVest</span>
    </span>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid={TESTIDS.footer.root}
      className="relative border-t border-iv-line bg-iv-bg pt-24 pb-10 overflow-hidden"
    >
      <div className="iv-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <div className="md:col-span-5">
            <FooterLockup />
            <p className="mt-6 font-serif-display text-2xl md:text-[26px] leading-snug text-iv-fg max-w-md">
              Investment Decision<br />Continuity.
            </p>
            <p className="mt-6 text-[13.5px] text-iv-fg-3 max-w-md leading-relaxed">
              Self-service access for investors evaluating opportunities and for companies substantiating impact and ESG claims for those investors.
            </p>
          </div>

          <FooterCol title="Pathways">
            <FooterLink to="/investors" testid="footer-link-investors">For investors</FooterLink>
            <FooterLink to="/companies" testid="footer-link-companies">For companies</FooterLink>
          </FooterCol>

          <FooterCol title="Access">
            <FooterLink to="/access" testid={TESTIDS.footer.linkPilot}>Enter the Test Workspace</FooterLink>
            <FooterLink to="/sample">Try the guided sample</FooterLink>
            <FooterLink to="/login" testid={TESTIDS.footer.linkSignIn}>Sign in</FooterLink>
          </FooterCol>

          <FooterCol title="Institution">
            <FooterLink to="/#why">Why ImpactVest</FooterLink>
            <FooterLink to="/legal/privacy">Privacy</FooterLink>
            <FooterLink to="/legal/retention">Data retention</FooterLink>
            <FooterLink to="/legal/terms">Terms</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
          </FooterCol>
        </div>

        <div className="mt-24 relative">
          <div className="font-serif-display text-[16vw] leading-[0.85] tracking-tighter text-transparent select-none pointer-events-none"
               style={{ WebkitTextStroke: "1px rgba(244,243,240,0.06)" }}
               aria-hidden>
            ImpactVest
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-iv-line flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
            © {year} Gaiosis Systems · All rights reserved
          </p>
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
            Confidential · Private, account-scoped pilot workspaces
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }) {
  return (
    <div className="md:col-span-2 lg:col-span-2">
      <div className="font-mono-tag text-iv-fg-3 mb-5">{title}</div>
      <ul className="space-y-3">{children}</ul>
    </div>
  );
}

function FooterLink({ to, children, testid }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="iv-link-underline text-[13.5px] text-iv-fg-2 hover:text-iv-fg"
      >
        {children}
      </Link>
    </li>
  );
}
